import { AccountBalance, EventAvailable, Launch } from '@mui/icons-material';
import { Autocomplete, Box, Snackbar, TextField } from '@mui/material';
import { useIsMutating } from '@tanstack/react-query';
import _ from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AccordionSection from '../../components/AccordionSection';
import ActivityLogsSection from '../../components/activityLogs/ActivityLogsSection';
import AttachmentsSection from '../../components/attachments/AttachmentsSection';
import AccountSearchAutocomplete from '../../components/common/AccountSearchAutocomplete/AccountSearchAutocomplete';
import EmployeeSearchAutocomplete from '../../components/common/EmployeeSearchAutocomplete/EmployeeSearchAutocomplete';
import FieldsSection from '../../components/common/FieldsSection/FieldsSection';
import ItemHeader from '../../components/common/ItemHeader/ItemHeader';
import MoreOptions from '../../components/common/MoreOptions/MoreOptions';
import TagsSection from '../../components/common/TagsSection/TagsSection';
import NotesSection from '../../components/notes/NotesSection';
import StepTable from '../../components/steps/StepTable';
import StartCondition from '../../components/tasks/StartCondition';
import TaskDuePill from '../../components/tasks/TaskDuePill';
import TaskStatusPill from '../../components/tasks/TaskStatusPill';
import { DisableContext } from '../../contexts/DisableContextProvider';
import { ROUTE_PATHS } from '../../routes/Routes';
import { useGetAccountById } from '../../services/react-query/accounts';
import { useGetAllEmployees } from '../../services/react-query/employees';
import { useGetAllEvents } from '../../services/react-query/events';
import { mutationKeys } from '../../services/react-query/mutationKeys';
import { useGetAllPreferences } from '../../services/react-query/preferences';
import { useGetAllTasks, useGetTaskById, useUpdateTask } from '../../services/react-query/tasks';
import { useArchiveTask } from '../../services/react-query/tasks/useArchiveTask';
import { useCreateTask } from '../../services/react-query/tasks/useCreateTask';
import { useDestroyTask } from '../../services/react-query/tasks/useDeleteTask';
import { useUpdateTaskStep } from '../../services/react-query/tasks/useUpdateTaskStep';
import {
  inputContainerStyling,
  inputDividerStyling,
  itemActivitySectionStyling,
  itemContainerStyling,
  itemInnerLayoutStyling,
  itemWrapperStyling,
} from '../../styles/sxStyling';
import { ActivityLogOwnerType } from '../../types/ActivityLog';
import { AttachmentOwnerType } from '../../types/Attachment';
import { Event } from '../../types/Event';
import { NoteableType } from '../../types/Note';
import { Preference, PREFERENCES_VALID_KEYS } from '../../types/Preference';
import { Step } from '../../types/Step';
import { Task, TaskStatus } from '../../types/Task';
import { UserConfigurableModule, UserSettingsCollapseKeys } from '../../types/User';
import { Loading } from '../../uikit';
import { errorPopulator, uuidFinder } from '../../utility/inputUtils';
import { taskBaseColor } from '../../utility/task';
import PreferencePicker from '../../components/preferences/picker/PreferencePicker';
import { deriveInitialStartCondition, shouldDisableSubmitForStartCondition, StartConditionType } from '../../utility/startCondition';
import usePermissionAllowed from '../../hooks/usePermissionAllowed';
import { Actions } from '../../services/react-query/permissions';

const TASK_STATUS_OPTIONS = [
  { label: 'Not Started', value: TaskStatus.NOT_STARTED },
  { label: 'Ready', value: TaskStatus.READY },
  { label: 'In Progress', value: TaskStatus.IN_PROGRESS },
  { label: 'On Hold', value: TaskStatus.ON_HOLD },
  { label: 'Completed', value: TaskStatus.COMPLETED },
];

export default function TaskItem() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { mutate: destroyTask } = useDestroyTask({ uuid: _.toString(uuid), onSuccessCallback: () => navigate(`/${ROUTE_PATHS.TASKS}`) });
  const { mutate: archiveTask } = useArchiveTask({ uuid: _.toString(uuid), onSuccessCallback: () => navigate(`/${ROUTE_PATHS.TASKS}`) });

  const [task, setTask] = useState<Task | null>(null);
  const [saved, setSaved] = useState<boolean>(false);
  const [startCondType, setStartCondType] = useState<StartConditionType>('manual');
  const [searchParams] = useSearchParams();
  const eventTemplateUuidFromParams = searchParams.get('template') || undefined;
  const accountUuidFromParams = searchParams.get('account') || undefined;

  const accountUuidFromTask = task?.account_uuid;
  const assignAccountForTaskUuid = accountUuidFromParams || accountUuidFromTask;

  const { data: taskData, isLoading: isGetTaskByIdLoading } = useGetTaskById({ uuid, template: eventTemplateUuidFromParams });
  const { mutate: updateStep } = useUpdateTaskStep();
  const { data: employeeData } = useGetAllEmployees({ params: { per_page: 0 } });

  // Preferences for TASK_TYPE / TASK_STATUS (status is rendered via TASK_STATUS_OPTIONS, but we still fetch prefs consistently)
  const { data: preferenceData, isFetching: isGetAllPreferencesFetching } = useGetAllPreferences({
    params: { filters: { keys: [PREFERENCES_VALID_KEYS.TASK_STATUS, PREFERENCES_VALID_KEYS.TASK_TYPE] } },
  });

  // Only load events when an account is chosen and no event already tied
  const { data: eventData, isLoading: isGetAllEventsLoading } = useGetAllEvents({
    params: { per_page: 0, filters: { account: [task?.account_uuid ?? ''] } },
    enabled: !!(task?.account_uuid && !task?.event_uuid),
  });

  const { data: existingAccountData } = useGetAccountById({
    uuid: _.toString(assignAccountForTaskUuid),
    enabled: !!assignAccountForTaskUuid,
  });

  const dependencyFilters = useMemo(() => {
    if (!task) return {};
    const defaultFilter = { status: [TaskStatus.NOT_STARTED, TaskStatus.READY, TaskStatus.IN_PROGRESS, TaskStatus.ON_HOLD] };
    if (task.event_uuid) return { ...defaultFilter, events: [task.event_uuid] };
    if (task.account_uuid) return { ...defaultFilter, account: [task.account_uuid] };
    return defaultFilter;
  }, [task?.event_uuid, task?.account_uuid]);

  const { data: dependencyTaskData } = useGetAllTasks({ params: { filters: dependencyFilters }, enabled: !(task?.start_date || task?.start_date_offset) });

  const { mutate: createTask } = useCreateTask({ onSuccessCallback: (data) => navigate(`/${ROUTE_PATHS.TASKS}/${data.uuid}`) });
  const { mutate: updateTask } = useUpdateTask({ uuid: _.toString(uuid), onSuccessCallback: () => setSaved(true) });
  const isCreateTaskMutation = useIsMutating({ mutationKey: [mutationKeys.createTask] });
  const isUpdateTaskMutation = useIsMutating({ mutationKey: [mutationKeys.updateTask] });

  useEffect(() => {
    if (taskData) {
      setTask({
        ...taskData,
        account_uuid: taskData.account_uuid || assignAccountForTaskUuid,
      });
      setStartCondType(
        deriveInitialStartCondition({
          uuid: taskData.uuid,
          start_date: taskData.start_date,
          start_date_offset: taskData.start_date_offset,
          depends_on_uuid: taskData.depends_on_uuid,
        })
      );
    }
  }, [taskData]);

  const { disabled, selfDisable } = useContext(DisableContext);
  selfDisable(!!task?.discarded_at);

  const isNew = uuid === 'new';

  const employees = employeeData?.data || [];
  const preferences = preferenceData || [];
  const events = eventData?.data || [];
  const filteredEvents = assignAccountForTaskUuid ? _.filter(events, { account_uuid: assignAccountForTaskUuid }) : events;

  if (!task || isGetTaskByIdLoading) return <Loading />;
  const canSubmit = !shouldDisableSubmitForStartCondition(task, startCondType);
  const handleSave = () => {
    if (!canSubmit) return;
    if (isNew) createTask(task);
    else updateTask(task);
  };

  const handleStepToggle = (step: Step, action: 'complete' | 'start') => {
    const completed = !step.completed_at;
    const started = !step.started_at;
    updateStep(
      { completed, uuid: step.uuid, started, step_action: action },
      {
        onSuccess: (updatedStep: Step) => {
          if (!task) return;
          setTask({
            ...task,
            status: updatedStep.task?.status || task.status,
            steps: _.map(task.steps, (s) => (s.uuid === updatedStep.uuid ? updatedStep : s)),
          });
        },
      }
    );
  };

  const updateTaskData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newTask = { ...task, [event.target.id]: event.target.value };
    setTask(newTask as Task);
  };

  const eventInput = (
    <Box sx={{ display: 'inline-flex', gap: 1 }}>
      {taskData?.event_uuid || !isNew ? (
        <TextField disabled label="Event" value={task.event_display_name ?? ''} />
      ) : (
        <Autocomplete<Event>
          fullWidth
          loading={isGetAllEventsLoading}
          options={filteredEvents}
          onChange={(_, ev) => setTask({ ...task, event_uuid: ev?.uuid, event_display_name: ev?.name } as Task)}
          getOptionLabel={(ev) => ev.name}
          value={uuidFinder(filteredEvents, task.event_uuid)}
          disabled={disabled || !task?.account_uuid}
          renderInput={(params) => (
            <TextField
              {...params}
              {...errorPopulator(task, 'events')}
              label="Event"
              placeholder={task?.account_uuid ? undefined : 'Please select an account first'}
            />
          )}
        />
      )}
      {task.event_uuid && (
        <Link to={`/${ROUTE_PATHS.EVENTS}/${task.event_uuid}`} style={{ alignContent: 'center' }}>
          <Launch sx={{ color: 'black' }} />
        </Link>
      )}
    </Box>
  );

  const accounts = existingAccountData ? [existingAccountData] : [];
  const accountInput = () => {
    const ev = uuidFinder(events, task.event_uuid);
    const showDisabledAccountInput = !!ev || (!!accountUuidFromParams && isNew) || (!!accountUuidFromTask && !isNew);

    if (showDisabledAccountInput) {
      const accountName = ev?.account_name || task.account_name || existingAccountData?.name || '';
      const accountUuid = ev?.account_uuid || accountUuidFromTask || existingAccountData?.uuid || '';
      return (
        <Box sx={{ display: 'inline-flex', gap: 1 }}>
          <TextField disabled label="Account" value={accountName} />
          {accountUuid && (
            <Link to={`/${ROUTE_PATHS.ACCOUNTS}/${accountUuid}`} style={{ alignContent: 'center' }}>
              <Launch sx={{ color: 'black' }} />
            </Link>
          )}
        </Box>
      );
    }

    return (
      <AccountSearchAutocomplete
        value={uuidFinder(accounts, assignAccountForTaskUuid)}
        onChange={(account) => setTask({ ...task, account_uuid: account?.uuid, account_name: account?.name } as Task)}
      />
    );
  };

  type PrefUuidKey = 'type_uuid';
  const setPrefUuid = (key: PrefUuidKey) => (p: Preference | null | undefined) => {
    setTask((prev) => (prev ? ({ ...prev, [key]: p?.uuid ?? undefined } as Task) : prev));
  };

  return (
    <Box sx={itemWrapperStyling}>
      <ItemHeader
        title={isNew ? 'Create Task' : taskData?.name || ''}
        onSave={handleSave}
        archived={!!task.discarded_at}
        disableActions={Boolean(isCreateTaskMutation || isUpdateTaskMutation || !canSubmit)}
        showMoreOptions={!isNew ? <MoreOptions onDelete={destroyTask} onArchive={archiveTask} name={task?.name} itemName="task" /> : null}
        module={UserConfigurableModule.TASKS}
        infoItems={[
          { icon: <EventAvailable fontSize="small" color="action" />, content: taskData?.event_display_name || '—' },
          { icon: <AccountBalance fontSize="small" color="action" />, content: taskData?.account_name || '—' },
        ]}
      />
      {/* Left Side */}
      <Box sx={itemInnerLayoutStyling}>
        <Box sx={itemContainerStyling}>
          <AccordionSection title="Task Information" collapseId={UserSettingsCollapseKeys.TASK_ITEM_INFORMATION}>
            <Box sx={inputContainerStyling}>
              <Box sx={inputDividerStyling}>
                {eventInput}
                <TextField id="name" label="Name" disabled={disabled} onChange={updateTaskData} value={task.name} {...errorPopulator(task, 'name')} />
                <TextField
                  id="description"
                  label="Description"
                  onChange={updateTaskData}
                  value={task.description}
                  disabled={disabled}
                  {...errorPopulator(task, 'description')}
                />
                <StartCondition
                  showLink
                  task={task}
                  setTask={setTask}
                  dependencyOptions={dependencyTaskData?.data || []}
                  disabledConditions={task.event_uuid ? [] : ['offset']}
                  onTypeChange={setStartCondType}
                  conditionType={startCondType}
                />
              </Box>

              <Box sx={inputDividerStyling}>
                {accountInput()}

                <PreferencePicker
                  label="Type"
                  options={_.filter(preferences, { key: PREFERENCES_VALID_KEYS.TASK_TYPE })}
                  loading={isGetAllPreferencesFetching}
                  value={uuidFinder(preferences, task.type_uuid) as Preference | null}
                  onValueChange={setPrefUuid('type_uuid')}
                  disabled={disabled}
                  error={!!errorPopulator(task, 'type')?.error}
                  helperText={errorPopulator(task, 'type')?.helperText}
                />

                <Box sx={{ display: 'inline-flex', gap: 1 }}>
                  <EmployeeSearchAutocomplete
                    isDisabled={disabled}
                    value={uuidFinder(employees, task.employee_uuid)}
                    onChange={(employee) => setTask({ ...task, employee_uuid: employee?.uuid })}
                    label="Assign To"
                    error={errorPopulator(task, 'employee')}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}>
                  <TextField
                    id="due_date"
                    label="Due Date"
                    type="date"
                    onChange={updateTaskData}
                    value={task.due_date ?? ''}
                    disabled={disabled}
                    {...errorPopulator(task, 'due_date')}
                  />
                  <TaskDuePill task={task} />
                </Box>

                <Autocomplete
                  options={TASK_STATUS_OPTIONS}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <TaskStatusPill status={option.value} />
                    </li>
                  )}
                  onChange={(_, option) => setTask({ ...task, status: option?.value || TaskStatus.NOT_STARTED })}
                  value={_.find(TASK_STATUS_OPTIONS, { value: task.status })}
                  disabled={disabled}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      {...errorPopulator(task, 'status')}
                      fullWidth
                      label="Status"
                      sx={{ background: `${taskBaseColor(task.status)}30` }}
                    />
                  )}
                />
              </Box>
            </Box>
          </AccordionSection>

          <FieldsSection
            fields={task.fields}
            setFields={(fields) => setTask({ ...task, fields })}
            collapseId={UserSettingsCollapseKeys.TASK_ITEM_CUSTOM_FIELDS}
          />

          <StepTable steps={task.steps} handleStepToggle={handleStepToggle} editPermitted={!disabled} taskUuid={task.uuid} />

          <TagsSection tags={task.tags} collapseId={UserSettingsCollapseKeys.TASK_ITEM_TAGS} setTags={(tags) => setTask({ ...task, tags })} />

          {!isNew && (
            <AttachmentsSection collapseId={UserSettingsCollapseKeys.TASK_ITEM_ATTACHMENTS} ownerType={AttachmentOwnerType.TASK} ownerUuid={uuid ?? ''} />
          )}

          <Snackbar open={saved} onClose={() => setSaved(false)} message="Saved!" />
        </Box>

        {/* Right Side */}
        {!isNew && (
          <Box sx={itemActivitySectionStyling}>
            <ActivityLogsSection ownerUuid={uuid} ownerType={ActivityLogOwnerType.TASK} collapseId={UserSettingsCollapseKeys.TASK_ITEM_ACTIVITY_LOG} />
            {uuid && <NotesSection noteable={{ noteableUuid: uuid, noteableType: NoteableType.TASK }} collapseId={UserSettingsCollapseKeys.TASK_ITEM_NOTES} />}
          </Box>
        )}
      </Box>
    </Box>
  );
}
